import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, ErrorBanner, Field, Input } from "@/components/ui-kit";
import { formatNaira, useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import {
  adminApplyLoanCounter,
  approveAndDisburseLoan,
  counterLoanOffer,
  declineLoan,
  fetchAllLoans,
  fetchLoanById,
  markInstallmentPaid,
  sendReminder,
} from "@/lib/data";
import {
  MIN_LOAN_DAYS,
  TIER_LABEL,
  formatDurationDays,
  maxLoanDaysForLevel,
  normalizeVerificationLevel,
  type Loan,
  type LoanStatus,
  type VerificationLevel,
} from "@/lib/types";

export const Route = createFileRoute("/admin/loans")({
  component: LoansPage,
});

const totalDue = (l: Loan) => l.proposedRepay ?? l.amount * (1 + l.interestRate / 100);

function statusTone(status: LoanStatus, isOverdue: boolean): "gold" | "success" | "danger" | "warning" | "neutral" {
  if (status === "active") return isOverdue ? "danger" : "gold";
  if (status === "repaid") return "success";
  if (status === "overdue" || status === "declined") return "danger";
  if (status === "countered") return "warning";
  if (status === "counter_accepted") return "gold";
  return "warning";
}

function LoansPage() {
  const { session } = useAuth();
  const isSuper = session?.kind === "admin" && session.role === "super";
  const adminId = session?.kind === "admin" ? session.id : "";
  const [loans, setLoans] = useState<Loan[]>([]);
  const [borrowers, setBorrowers] = useState<
    Record<string, { name: string; email: string }>
  >({});
  const [levels, setLevels] = useState<Record<string, VerificationLevel>>({});
  const [filter, setFilter] = useState<"all" | LoanStatus>("all");
  const [detailLoan, setDetailLoan] = useState<Loan | null>(null);
  const [declineFor, setDeclineFor] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState("");
  const [disbursingId, setDisbursingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [counterFor, setCounterFor] = useState<Loan | null>(null);
  const [counterPrincipal, setCounterPrincipal] = useState("");
  const [counterRepayAmt, setCounterRepayAmt] = useState("");
  const [counterDays, setCounterDays] = useState("");
  const [counterNote, setCounterNote] = useState("");
  const [counterBusy, setCounterBusy] = useState(false);

  const refresh = useCallback(async () => {
    const rows = await fetchAllLoans();
    setLoans(rows);
    if (detailLoan) {
      const fresh = await fetchLoanById(detailLoan.id);
      if (fresh) setDetailLoan(fresh);
    }
    const ids = [...new Set(rows.map((l) => l.borrowerId))];
    if (ids.length) {
      const [{ data: profileRows }, { data: settingsRows }] = await Promise.all([
        supabase.from("profiles").select("id, full_name, email").in("id", ids),
        supabase.from("borrower_settings").select("user_id, verification_level").in("user_id", ids),
      ]);
      const map: Record<string, { name: string; email: string }> = {};
      for (const p of profileRows ?? []) {
        map[String(p.id)] = {
          name: String(p.full_name ?? "").trim() || "—",
          email: String(p.email ?? "").trim(),
        };
      }
      setBorrowers(map);
      const levelMap: Record<string, VerificationLevel> = {};
      for (const s of settingsRows ?? []) {
        levelMap[String(s.user_id)] = normalizeVerificationLevel(s.verification_level);
      }
      setLevels(levelMap);
    }
  }, [detailLoan]);

  useEffect(() => {
    refresh().catch((e) => setActionError(e instanceof Error ? e.message : "Failed to load loans"));
  }, []);

  useEffect(() => {
    const channel = supabase
      .channel("loans-changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "loans" }, () => { void refresh(); })
      .on("postgres_changes", { event: "*", schema: "public", table: "repayment_schedule" }, () => { void refresh(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [refresh]);

  const filtered = loans
    .slice()
    .filter((l) => (filter === "all" ? true : l.status === filter));

  const approve = async (id: string) => {
    const loan = loans.find((x) => x.id === id);
    if (!loan) return;
    setActionError(null);
    setDisbursingId(id);
    try {
      await approveAndDisburseLoan(id, loan.borrowerId);
      setToast("Transfer successful");
      await refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Disbursement failed.");
      await refresh();
    } finally {
      setDisbursingId(null);
    }
  };

  const confirmDecline = async () => {
    if (!declineFor) return;
    const l = loans.find((x) => x.id === declineFor);
    if (!l) return;
    try {
      await declineLoan(declineFor, declineReason || "Not approved", l.borrowerId);
      setDeclineFor(null);
      setDeclineReason("");
      await refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Decline failed.");
    }
  };

  const openCounter = (loan: Loan) => {
    setActionError(null);
    setCounterFor(loan);
    const principal = loan.status === "countered" && loan.counterPrincipal != null
      ? loan.counterPrincipal
      : loan.amount;
    const repay = loan.status === "countered" && loan.counterRepay != null
      ? loan.counterRepay
      : Math.round(totalDue(loan));
    const days = loan.status === "countered" && loan.counterDurationDays != null
      ? loan.counterDurationDays
      : loan.durationDays;
    setCounterPrincipal(String(principal));
    setCounterRepayAmt(String(repay));
    setCounterDays(String(days));
    setCounterNote("");
  };

  const applyCounterAndDisburse = async (loan: Loan) => {
    setActionError(null);
    setDisbursingId(loan.id);
    try {
      await adminApplyLoanCounter(loan.id);
      await approveAndDisburseLoan(loan.id, loan.borrowerId);
      setToast("Counter accepted and transfer started");
      await refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Could not apply counter / disburse.");
      await refresh();
    } finally {
      setDisbursingId(null);
    }
  };

  const submitCounter = async () => {
    if (!counterFor) return;
    const principal = Number(counterPrincipal);
    const repay = Number(counterRepayAmt);
    const days = Number(counterDays);
    const counterLevel = levels[counterFor.borrowerId] ?? "express";
    const maxDays = maxLoanDaysForLevel(counterLevel);
    if (!principal || principal <= 0) return setActionError("Enter a valid principal amount.");
    if (!repay || repay < principal) return setActionError("Repay amount must be at least the principal.");
    if (!days || days < MIN_LOAN_DAYS || days > maxDays) {
      return setActionError(`Duration must be between ${MIN_LOAN_DAYS} and ${maxDays} days.`);
    }
    setCounterBusy(true);
    setActionError(null);
    try {
      await counterLoanOffer({
        loanId: counterFor.id,
        borrowerId: counterFor.borrowerId,
        principal,
        repay,
        durationDays: days,
        note: counterNote.trim() || undefined,
      });
      setCounterFor(null);
      await refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Counter-offer failed.");
    } finally {
      setCounterBusy(false);
    }
  };

  const remind = async (loan: Loan, channel: "email" | "whatsapp") => {
    setActionError(null);
    try {
      const res = await sendReminder({
        borrowerId: loan.borrowerId,
        loanId: loan.id,
        channel,
        customMessage: `Reminder: loan ${loan.id.slice(0, 8).toUpperCase()} of ${formatNaira(totalDue(loan) - loan.paid)} is due ${new Date(loan.dueDate).toLocaleDateString()}.`,
      });
      if (res?.whatsapp_link) window.open(res.whatsapp_link, "_blank");
      setToast("Reminder sent");
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Reminder failed.");
    }
  };

  const now = Date.now();

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Operations</div>
          <h1 className="font-display text-4xl mt-1">Loans</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          {(["all", "pending", "countered", "counter_accepted", "approved", "active", "overdue", "repaid", "declined"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-md text-xs uppercase tracking-wider border transition ${
                filter === f ? "border-gold text-gold bg-gold/10" : "border-border text-muted-foreground hover:border-gold/40"
              }`}
            >
              {f.replace("_", " ")}
            </button>
          ))}
        </div>
      </div>

      <ErrorBanner message={actionError} />
      {toast && (
        <div className="rounded-md border border-success/40 bg-success/10 px-3 py-2 text-sm text-success">{toast}</div>
      )}

      <Card className="!p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border bg-accent/30">
                <th className="py-3 px-4">Loan</th>
                <th className="py-3 px-4">Borrower</th>
                <th className="py-3 px-4">Amount</th>
                <th className="py-3 px-4">Duration</th>
                <th className="py-3 px-4">Remaining</th>
                <th className="py-3 px-4">Due</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => {
                const remaining = totalDue(l) - l.paid;
                const isOverdue = new Date(l.dueDate).getTime() < now && l.status === "active";
                const level = levels[l.borrowerId];
                return (
                  <tr key={l.id} className="border-b border-border/40 hover:bg-accent/20">
                    <td className="py-3 px-4 font-mono text-xs text-muted-foreground">{l.id.slice(0, 8).toUpperCase()}</td>
                    <td className="py-3 px-4">
                      <div className="font-medium">{borrowers[l.borrowerId]?.name ?? "—"}</div>
                      {borrowers[l.borrowerId]?.email && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {borrowers[l.borrowerId].email}
                        </div>
                      )}
                      {level && level !== "none" && (
                        <div className="text-[10px] uppercase tracking-wider text-gold mt-0.5">{TIER_LABEL[level]}</div>
                      )}
                    </td>
                    <td className="py-3 px-4">{formatNaira(l.amount)}</td>
                    <td className="py-3 px-4">
                      <div>{formatDurationDays(l.durationDays, { capAt30: false })}</div>
                      <div className="text-xs text-muted-foreground">{l.durationDays} day{l.durationDays === 1 ? "" : "s"}</div>
                    </td>
                    <td className="py-3 px-4">{formatNaira(remaining)}</td>
                    <td className="py-3 px-4">
                      <div>{new Date(l.dueDate).toLocaleDateString()}</div>
                      {isOverdue && <div className="text-xs text-destructive">Past due</div>}
                    </td>
                    <td className="py-3 px-4">
                      <Badge tone={statusTone(l.status, isOverdue)}>
                        {l.status.replace("_", " ")}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-right space-x-2 whitespace-nowrap">
                      <Button variant="outline" onClick={() => setDetailLoan(l)}>View</Button>
                      {isSuper && (l.status === "pending" || l.status === "approved" || l.status === "counter_accepted" || l.status === "countered") && (
                        <>
                          {(l.status === "pending" || l.status === "approved" || l.status === "counter_accepted") && (
                            <Button variant="gold" loading={disbursingId === l.id} onClick={() => approve(l.id)}>
                              {disbursingId === l.id
                                ? "Processing transfer..."
                                : l.status === "approved"
                                  ? "Retry disbursement"
                                  : l.status === "counter_accepted"
                                    ? "Final approve & disburse"
                                    : "Approve & Disburse"}
                            </Button>
                          )}
                          {l.status === "countered" && (
                            <Button
                              variant="gold"
                              loading={disbursingId === l.id}
                              onClick={() => applyCounterAndDisburse(l)}
                            >
                              {disbursingId === l.id ? "Processing..." : "Accept counter & disburse"}
                            </Button>
                          )}
                          {(l.status === "pending" || l.status === "countered") && (
                            <>
                              <Button variant="outline" onClick={() => openCounter(l)}>
                                {l.status === "countered" ? "Counter again" : "Counter offer"}
                              </Button>
                              <Button variant="danger" onClick={() => setDeclineFor(l.id)}>Decline</Button>
                            </>
                          )}
                        </>
                      )}
                      {(l.status === "active" || isOverdue) && (
                        <>
                          <Button variant="ghost" onClick={() => remind(l, "email")}>Email</Button>
                          <Button variant="ghost" onClick={() => remind(l, "whatsapp")}>WhatsApp</Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="py-12 text-center text-muted-foreground">No loans match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {!isSuper && <p className="text-xs text-muted-foreground">Manager role can mark installments paid but cannot approve loans or change terms.</p>}

      {detailLoan && (
        <LoanDetail
          loan={detailLoan}
          borrowerName={borrowers[detailLoan.borrowerId]?.name}
          borrowerEmail={borrowers[detailLoan.borrowerId]?.email}
          level={levels[detailLoan.borrowerId]}
          adminId={adminId}
          isSuper={isSuper}
          disbursing={disbursingId === detailLoan.id}
          onClose={() => setDetailLoan(null)}
          onChange={refresh}
          onApprove={() => approve(detailLoan.id)}
          onApplyCounter={() => applyCounterAndDisburse(detailLoan)}
          onCounter={() => {
            openCounter(detailLoan);
          }}
          onDecline={() => setDeclineFor(detailLoan.id)}
        />
      )}

      {declineFor && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-navy-deep/70 backdrop-blur-sm" onClick={() => setDeclineFor(null)}>
          <div className="w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <Card>
              <h3 className="font-display text-xl">Decline loan {declineFor.slice(0, 8).toUpperCase()}</h3>
              <div className="mt-4 space-y-3">
                <Field label="Reason">
                  <Input value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} placeholder="e.g. Insufficient bank inflow" />
                </Field>
                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" onClick={() => setDeclineFor(null)}>Cancel</Button>
                  <Button variant="danger" onClick={confirmDecline}>Confirm decline</Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {counterFor && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-navy-deep/70 backdrop-blur-sm" onClick={() => setCounterFor(null)}>
          <div className="w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <Card>
              <h3 className="font-display text-xl">Counter-offer for loan {counterFor.id.slice(0, 8).toUpperCase()}</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Requested {formatNaira(counterFor.amount)} · {formatDurationDays(counterFor.durationDays, { capAt30: false })} ({counterFor.durationDays} days) · due {new Date(counterFor.dueDate).toLocaleDateString()} · repay {formatNaira(totalDue(counterFor))}
              </p>
              <div className="mt-4 space-y-3">
                <Field label="Principal (NGN)">
                  <Input
                    type="number"
                    value={counterPrincipal}
                    onChange={(e) => setCounterPrincipal(e.target.value)}
                    inputMode="numeric"
                  />
                </Field>
                <Field label="Repay amount (NGN)">
                  <Input
                    type="number"
                    value={counterRepayAmt}
                    onChange={(e) => setCounterRepayAmt(e.target.value)}
                    inputMode="numeric"
                  />
                </Field>
                <Field
                  label={`Duration in days (${MIN_LOAN_DAYS}–${maxLoanDaysForLevel(levels[counterFor.borrowerId] ?? "express")})`}
                >
                  <Input
                    type="number"
                    min={MIN_LOAN_DAYS}
                    max={maxLoanDaysForLevel(levels[counterFor.borrowerId] ?? "express")}
                    value={counterDays}
                    onChange={(e) => setCounterDays(e.target.value)}
                    inputMode="numeric"
                  />
                </Field>
                <Field label="Note to borrower (optional)">
                  <Input value={counterNote} onChange={(e) => setCounterNote(e.target.value)} placeholder="e.g. Reduced principal based on limit" />
                </Field>
                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" onClick={() => setCounterFor(null)}>Cancel</Button>
                  <Button variant="gold" loading={counterBusy} onClick={submitCounter}>Send counter-offer</Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

function LoanDetail({
  loan,
  borrowerName,
  borrowerEmail,
  level,
  adminId,
  isSuper,
  disbursing,
  onClose,
  onChange,
  onApprove,
  onApplyCounter,
  onCounter,
  onDecline,
}: {
  loan: Loan;
  borrowerName?: string;
  borrowerEmail?: string;
  level?: VerificationLevel;
  adminId: string;
  isSuper: boolean;
  disbursing: boolean;
  onClose: () => void;
  onChange: () => Promise<void>;
  onApprove: () => void;
  onApplyCounter: () => void;
  onCounter: () => void;
  onDecline: () => void;
}) {
  const { session } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [marking, setMarking] = useState<string | null>(null);

  const markPaid = async (instId: string) => {
    setError(null);
    setMarking(instId);
    try {
      await markInstallmentPaid({
        installmentId: instId,
        loanId: loan.id,
        borrowerId: loan.borrowerId,
        adminId,
      });
      await onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not mark this installment paid.");
    } finally {
      setMarking(null);
    }
  };

  const hasCounter = loan.counterPrincipal != null || loan.counterRepay != null || loan.counterDurationDays != null;
  const negotiating = loan.status === "pending" || loan.status === "countered" || loan.status === "counter_accepted";

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-navy-deep/70 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-xl bg-card border-l border-border overflow-y-auto p-6 space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Loan {loan.id.slice(0, 8).toUpperCase()}</div>
            <h2 className="font-display text-2xl mt-1">{borrowerName ?? "—"}</h2>
            {borrowerEmail && (
              <div className="text-sm text-muted-foreground mt-0.5">{borrowerEmail}</div>
            )}
            <div className="text-sm text-muted-foreground">
              {formatNaira(loan.amount)} · repay {formatNaira(totalDue(loan))}
            </div>
            <div className="mt-2 flex gap-2 flex-wrap">
              {level && level !== "none" && <Badge tone="gold">{TIER_LABEL[level]}</Badge>}
              <Badge tone={statusTone(loan.status, false)}>{loan.status.replace("_", " ")}</Badge>
              {loan.proposedRepay != null && (
                <Badge tone="neutral">Proposed repay {formatNaira(loan.proposedRepay)}</Badge>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-2xl leading-none">×</button>
        </div>

        <Card>
          <h3 className="font-display text-lg">Chosen terms</h3>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
            <Field label="Duration">
              <div className="text-foreground">
                {formatDurationDays(loan.durationDays, { capAt30: false })}
                <span className="text-muted-foreground"> ({loan.durationDays} days)</span>
              </div>
            </Field>
            <Field label="Due date">
              <div className="text-foreground">{new Date(loan.dueDate).toLocaleDateString()}</div>
            </Field>
            <Field label="Amount">
              <div className="text-foreground">{formatNaira(loan.amount)}</div>
            </Field>
            <Field label="Total to repay">
              <div className="text-foreground">{formatNaira(totalDue(loan))}</div>
            </Field>
          </div>
        </Card>

        {isSuper && negotiating && (
          <Card>
            <h3 className="font-display text-lg">Your decision</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Negotiate with Counter, or use Accept / Final approve when terms are agreed.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {loan.status === "pending" && (
                <Button variant="gold" loading={disbursing} onClick={onApprove}>
                  Approve & Disburse
                </Button>
              )}
              {loan.status === "counter_accepted" && (
                <Button variant="gold" loading={disbursing} onClick={onApprove}>
                  Final approve & disburse
                </Button>
              )}
              {loan.status === "countered" && (
                <Button variant="gold" loading={disbursing} onClick={onApplyCounter}>
                  Accept counter & disburse
                </Button>
              )}
              {(loan.status === "pending" || loan.status === "countered") && (
                <>
                  <Button variant="outline" onClick={onCounter}>
                    {loan.status === "countered" ? "Counter again" : "Counter offer"}
                  </Button>
                  <Button variant="danger" onClick={onDecline}>Decline</Button>
                </>
              )}
            </div>
          </Card>
        )}

        {hasCounter && (
          <Card>
            <h3 className="font-display text-lg">Counter-offer</h3>
            <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
              {loan.counterPrincipal != null && <Field label="Principal"><div className="text-foreground">{formatNaira(loan.counterPrincipal)}</div></Field>}
              {loan.counterRepay != null && <Field label="Repay"><div className="text-foreground">{formatNaira(loan.counterRepay)}</div></Field>}
              {loan.counterDurationDays != null && <Field label="Duration"><div className="text-foreground">{formatDurationDays(loan.counterDurationDays)}</div></Field>}
              {loan.counterRate != null && <Field label="Rate"><div className="text-foreground">{loan.counterRate}%</div></Field>}
            </div>
            {loan.counterNote && (
              <p className="mt-3 text-sm text-muted-foreground">Note: {loan.counterNote}</p>
            )}
          </Card>
        )}

        <Card>
          <h3 className="font-display text-lg">Installments</h3>
          <ErrorBanner message={error} />
          <div className="mt-3 space-y-2">
            {loan.installments.length === 0 ? (
              <p className="text-sm text-muted-foreground">No schedule yet — created after disbursement.</p>
            ) : (
              loan.installments.map((inst, i) => (
                <div key={inst.id} className="flex items-center justify-between gap-3 p-3 rounded-md border border-border bg-accent/20">
                  <div>
                    <div className="text-sm">#{i + 1} · {formatNaira(inst.amount)}</div>
                    <div className="text-xs text-muted-foreground">Due {new Date(inst.dueDate).toLocaleDateString()}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge tone={inst.paid ? "success" : new Date(inst.dueDate).getTime() < Date.now() ? "danger" : "neutral"}>
                      {inst.paid ? "Paid" : new Date(inst.dueDate).getTime() < Date.now() ? "Overdue" : "Upcoming"}
                    </Badge>
                    {!inst.paid && session?.kind === "admin" && (
                      <Button variant="gold" loading={marking === inst.id} onClick={() => markPaid(inst.id)}>Mark paid</Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        {loan.declineReason && (
          <Card>
            <h3 className="font-display text-lg">Decline reason</h3>
            <p className="mt-2 text-sm text-muted-foreground">{loan.declineReason}</p>
          </Card>
        )}
      </div>
    </div>
  );
}
